import React, { useState, useEffect, useMemo, useRef } from "react";
import { HashLink as Link } from "react-router-hash-link";
import axios from "axios";
import toast from "react-hot-toast";

const BED_OPTIONS = ["King Bed", "Twin Beds", "No Preference"];

const SPECIAL_REQ_OPTIONS = [
  "Accessibility Needs",
  "Dietary Requirements",
  "Smoking / Non-Smoking",
  "Other",
];

const getStarRatingDisplay = (starRating) => {
  const stars = {
    five_star: "★★★★★ 5-Star",
    four_star: "★★★★☆ 4-Star",
    three_star: "★★★☆☆ 3-Star",
    two_star: "★★☆☆☆ 2-Star",
    one_star: "★☆☆☆☆ 1-Star",
  };
  return stars[starRating] || "Rating Not Available";
};

const AccommodationPrices = ({ roomPrices }) => {
  const calculateNights = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return 0;

    const [y1, m1, d1] = String(checkIn).split("-").map(Number);
    const [y2, m2, d2] = String(checkOut).split("-").map(Number);

    if (!y1 || !m1 || !d1 || !y2 || !m2 || !d2) return 0;

    const inUTC = Date.UTC(y1, m1 - 1, d1);
    const outUTC = Date.UTC(y2, m2 - 1, d2);

    const nights = Math.ceil((outUTC - inUTC) / (1000 * 60 * 60 * 24));
    return nights > 0 ? nights : 0;
  };

  const [allRoomData, setAllRoomData] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState("");
  const [hotelPrices, setHotelPrices] = useState({
    single: null,
    double: null,
    triple: null,
  });
  const [loadingHotels, setLoadingHotels] = useState(true);

  const [isRegistrationClosed, setIsRegistrationClosed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRoomType, setSelectedRoomType] = useState(null);
  const [conferenceId, setConferenceId] = useState(null);
  const [showForm, setShowForm] = useState(true);
  const [showInvoice, setShowInvoice] = useState(false);
  const [bookingData, setBookingData] = useState(null);

  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const countryDropdownRef = useRef(null);

  const [closingDates, setClosingDates] = useState([]);
  const [closingDatesLoading, setClosingDatesLoading] = useState(true);

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    country: "",
    check_in: "",
    check_out: "",
    guests: 1,
    room_type: "",
    hotel_choice: "",
    bed_type: "",
    special_requirements: "",
    other_text: "",
    terms_accepted: false,
  });

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const API_URL = "https://eventscons.com/eventbackendnew/api/book-accommodation";
  const PAYMENT_API_URL = "https://eventscons.com/eventbackendnew/api/create-payment";
  const COUNTRIES_API = "https://eventscons.com/eventbackendnew/api/countries";

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    return dateStr.replace(/\.000000Z$/, "").slice(0, 19);
  };

  useEffect(() => {
    const pathname = window.location.pathname;
    const match = pathname.match(/conference\/details\/(\d+)/);
    if (match) {
      setConferenceId(Number(match[1]));
    }
  }, []);

  useEffect(() => {
    const fetchClosingDates = async () => {
      try {
        setClosingDatesLoading(true);

        const pathname = window.location.pathname;
        const match = pathname.match(/conference\/details\/(\d+)/);
        const confId = match ? Number(match[1]) : null;

        if (!confId) {
          setClosingDatesLoading(false);
          return;
        }

        const CLOSING_DATE_API = `https://eventscons.com/eventbackendnew/api/closing/date/${confId}`;
        const res = await axios.get(CLOSING_DATE_API, {
          headers: { Accept: "application/json" },
        });

        const apiData = res?.data?.data ?? res?.data;
        const arr = Array.isArray(apiData) ? apiData : [];
        setClosingDates(arr);

        const accommodationClosing = arr.find(
          (item) => String(item?.type || "").toLowerCase() === "accommodation"
        );

        if (accommodationClosing && accommodationClosing.closing_date) {
          const closingDateUTC = new Date(accommodationClosing.closing_date);
          const nowUTC = new Date();
          const isClosed = closingDateUTC < nowUTC;
          setIsRegistrationClosed(isClosed);
        } else {
          setIsRegistrationClosed(false);
        }
      } catch (err) {
        setClosingDates([]);
        setIsRegistrationClosed(false);
      } finally {
        setClosingDatesLoading(false);
      }
    };

    fetchClosingDates();
  }, []);

  useEffect(() => {
    const fetchRoomPrices = async () => {
      if (!conferenceId) return;

      try {
        setLoadingHotels(true);

        const ROOM_PRICES_API = `https://eventscons.com/eventbackendnew/api/room-prices/conference/${conferenceId}`;

        const res = await axios.get(ROOM_PRICES_API, {
          headers: { Accept: "application/json" },
        });

        if (res.data.success) {
          const data = res.data.data || [];
          setAllRoomData(data);

          const uniqueHotels = [...new Set(data.map((item) => item.hotel_name))];
          setHotels(uniqueHotels);

          if (uniqueHotels.length > 0) {
            setSelectedHotel(uniqueHotels[0]);
          }
        }
      } catch (error) {
        toast.error("Failed to load hotel data.");
      } finally {
        setLoadingHotels(false);
      }
    };

    fetchRoomPrices();
  }, [conferenceId]);

  const getHotelStarRating = (hotelName) => {
    const hotelData = allRoomData.find((item) => item.hotel_name === hotelName);
    return hotelData?.star_rating || null;
  };

  const handleHotelChange = (e) => {
    const hotelName = e.target.value;
    setSelectedHotel(hotelName);
  };

  useEffect(() => {
    if (selectedHotel && allRoomData.length > 0) {
      const hotelData = allRoomData.filter(
        (item) => item.hotel_name === selectedHotel
      );

      const single = hotelData.find((item) => item.room_type === "Single")?.base_price;
      const double = hotelData.find((item) => item.room_type === "Double")?.base_price;
      const triple = hotelData.find((item) => item.room_type === "Triple")?.base_price;

      setHotelPrices({
        single: single ? `$${parseFloat(single).toFixed(2)}` : null,
        double: double ? `$${parseFloat(double).toFixed(2)}` : null,
        triple: triple ? `$${parseFloat(triple).toFixed(2)}` : null,
      });
    }
  }, [selectedHotel, allRoomData]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoadingCountries(true);
        const res = await axios.get(COUNTRIES_API, {
          headers: { Accept: "application/json" },
        });

        const list = res.data?.data || [];
        setCountries(list);

        if (list.length) {
          setFormData((p) => ({
            ...p,
            country: p.country || list[0].name,
          }));
        }
      } catch (e) {
        toast.error("Failed to load countries.");
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!countryDropdownRef.current) return;
      if (!countryDropdownRef.current.contains(e.target)) {
        setIsCountryOpen(false);
      }
    };

    const onEsc = (e) => {
      if (e.key === "Escape") setIsCountryOpen(false);
    };

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);

    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  const latestAccommodationClosingDate = useMemo(() => {
    const accommodationItems = closingDates.filter(
      (item) => String(item?.type || "").toLowerCase() === "accommodation"
    );

    if (accommodationItems.length === 0) return null;

    let latest = null;
    for (const it of accommodationItems) {
      const d = new Date(it?.closing_date);
      if (!isNaN(d.getTime())) {
        if (!latest) latest = it;
        else if (d > new Date(latest.closing_date)) latest = it;
      }
    }
    return latest;
  }, [closingDates]);

  const getAmountByRoomType = (roomType) => {
    if (!selectedHotel || !allRoomData.length) return 0;

    let price = 0;

    if (roomType === "Single Room") {
      price = allRoomData.find(
        (item) => item.hotel_name === selectedHotel && item.room_type === "Single"
      )?.base_price;
    } else if (roomType === "Double Room") {
      price = allRoomData.find(
        (item) => item.hotel_name === selectedHotel && item.room_type === "Double"
      )?.base_price;
    } else if (roomType === "Triple Room") {
      price = allRoomData.find(
        (item) => item.hotel_name === selectedHotel && item.room_type === "Triple"
      )?.base_price;
    }

    return price ? parseFloat(price) : 0;
  };

  const nights = useMemo(
    () => calculateNights(formData.check_in, formData.check_out),
    [formData.check_in, formData.check_out]
  );

  const perNightAmount = useMemo(
    () => (selectedRoomType ? getAmountByRoomType(selectedRoomType) : 0),
    [selectedRoomType, selectedHotel, allRoomData]
  );

  const totalAmount = useMemo(
    () => (perNightAmount && nights ? perNightAmount * nights : 0),
    [perNightAmount, nights]
  );

  const selectedCountryObj = useMemo(() => {
    return countries.find((c) => c.name === formData.country) || null;
  }, [countries, formData.country]);

  const openModalForRoom = (roomType) => {
    if (isRegistrationClosed) {
      toast.error("Registration is closed. Date has expired.");
      return;
    }

    if (!selectedHotel) {
      toast.error("Please select a hotel first.");
      return;
    }

    setSelectedRoomType(roomType);
    setShowForm(true);
    setShowInvoice(false);
    setBookingData(null);

    setFormData({
      full_name: "",
      email: "",
      phone: "",
      country: countries[0]?.name || "",
      check_in: "",
      check_out: "",
      guests: 1,
      room_type: roomType,
      hotel_choice: selectedHotel,
      bed_type: "",
      special_requirements: "",
      other_text: "",
      terms_accepted: false,
    });

    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRoomType(null);
    setShowForm(true);
    setShowInvoice(false);
    setBookingData(null);
    setIsCountryOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "special_requirements" && value !== "Other") {
        next.other_text = "";
      }

      return next;
    });
  };

  const selectCountry = (name) => {
    setFormData((p) => ({ ...p, country: name }));
    setIsCountryOpen(false);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (
      isRegistrationClosed ||
      !formData.terms_accepted ||
      !conferenceId ||
      !formData.room_type ||
      !formData.check_in ||
      !formData.check_out ||
      !formData.hotel_choice
    ) {
      toast.error("Please fill all required fields.");
      return;
    }

    const n = calculateNights(formData.check_in, formData.check_out);

    if (!n) {
      toast.error("Please select valid check-in/check-out dates.");
      return;
    }

    const perNight = getAmountByRoomType(formData.room_type);

    if (!perNight) {
      toast.error("Room price not available.");
      return;
    }

    const finalTotal = Number(perNight) * n;

    try {
      setSubmitting(true);

      const payload = {
        conference_id: conferenceId,
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        country: formData.country,
        check_in: formData.check_in,
        check_out: formData.check_out,
        guests: Number(formData.guests),
        room_type: formData.room_type,
        hotel_choice: formData.hotel_choice,
        bed_type: formData.bed_type || "No Preference",
        special_requirements:
          formData.special_requirements === "Other"
            ? formData.other_text || "Other"
            : formData.special_requirements,
        terms_accepted: formData.terms_accepted ? 1 : 0,
        amount: Number(finalTotal),
        nights: Number(n),
        per_night: Number(perNight),
      };

      const res = await axios.post(API_URL, payload, {
        headers: { "Content-Type": "application/json" },
      });

      toast.success("Booking created successfully!");

      setBookingData({
        ...payload,
        booking_id: res.data?.booking_id || res.data?.id || `BK${Date.now()}`,
        booking_date: new Date().toISOString().split("T")[0],
      });

      setShowForm(false);
      setShowInvoice(true);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create booking.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayNow = async () => {
    if (!bookingData) return;

    try {
      setLoading(true);

      const paymentResponse = await axios.post(PAYMENT_API_URL, {
        amount: bookingData.amount,
        return_url: "https://www.google.com",
        cancel_url: "https://www.google.com",
      });

      const paymentData = paymentResponse.data;

      if (paymentData.approval_url) {
        window.location.href = paymentData.approval_url;
      } else {
        toast.error("No payment URL received");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Payment initiation failed");
    } finally {
      setLoading(false);
    }
  };

  const RegisterButton = ({
    onClick,
    children = "Register",
    className = "",
    disabled = false,
  }) => (
    <div className="btn-box">
      {disabled ? (
        <span
          className={`theme-btn btn-style-one ${className}`}
          style={{
            backgroundColor: "#ccc",
            color: "#666",
            cursor: "not-allowed",
            opacity: 0.6,
          }}
        >
          Registration Closed
        </span>
      ) : (
        <button
          type="button"
          className={`theme-btn btn-style-one ${className}`}
          onClick={onClick}
        >
          <span className="btn-title">{children}</span>
        </button>
      )}
    </div>
  );

  return (
    <>
      <section className={`pricing-section-two ${"alternate"}`}>
        <div className="sec-title text-center">
          <h2
            style={{
              display: "inline-block",
              borderBottom: "2px solid #9B1321",
              paddingBottom: "10px",
            }}
          >
            Accommodation Prices
          </h2>

          <div style={{ marginTop: "18px" }}>
            <div
              style={{
                display: "inline-block",
                padding: "10px 18px",
                borderRadius: "10px",
                border: "1px solid #9B1321",
                background: "#fff5f6",
                color: "#9B1321",
                fontWeight: 700,
                marginBottom: "10px",
              }}
            >
              Accommodation Closing Date
            </div>

            {closingDatesLoading ? (
              <div style={{ color: "#666", fontStyle: "italic" }}>Loading...</div>
            ) : latestAccommodationClosingDate ? (
              <>
                <div style={{ fontSize: "16px", fontWeight: 600, color: "#222" }}>
                  Closing: {formatDateTime(latestAccommodationClosingDate.closing_date)}
                </div>

                {isRegistrationClosed && (
                  <div
                    style={{
                      backgroundColor: "#fee2e2",
                      color: "#dc2626",
                      padding: "10px 18px",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: 600,
                      marginTop: "12px",
                      border: "1px solid #fecaca",
                    }}
                  >
                    Registration Closed
                  </div>
                )}
              </>
            ) : (
              <div style={{ color: "#666", fontStyle: "italic" }}>
                No closing date found.
              </div>
            )}
          </div>
        </div>

        <div className="auto-container">
          <div className="outer-box">
            <div className="pricing-block-two col-sm-12" style={{ marginBottom: "30px" }}>
              <div className="inner-box">
                <label
                  style={{
                    fontSize: "20px",
                    fontWeight: 700,
                    marginBottom: "15px",
                    display: "block",
                    textAlign: "center",
                    color: "#9B1321",
                  }}
                >
                  Choose Hotel Type
                </label>

                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    maxWidth: "500px",
                    margin: "0 auto",
                  }}
                >
                  <select
                    value={selectedHotel}
                    onChange={handleHotelChange}
                    className="form-control"
                    style={{
                      padding: "18px 50px 18px 20px",
                      fontSize: "18px",
                      borderRadius: "15px",
                      border: "3px solid #9B1321",
                      background: "#fff",
                      width: "100%",
                      textAlign: "center",
                      fontWeight: "600",
                      boxShadow: "0 5px 15px rgba(155,19,33,0.2)",
                    }}
                    disabled={loadingHotels || hotels.length === 0}
                  >
                    <option value="">
                      {loadingHotels ? "Loading hotels..." : "Choose your hotel"}
                    </option>
                    {hotels.map((hotel, index) => (
                      <option key={index} value={hotel}>
                        {hotel}
                      </option>
                    ))}
                  </select>

                  {loadingHotels && (
                    <div
                      style={{
                        position: "absolute",
                        right: "25px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#9B1321",
                        fontSize: "20px",
                      }}
                    >
                      Loading...
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row">
              <div className="pricing-block-two col-sm-12">
                <div className="inner-box">
                  <div className="price-box">
                    <h4
                      style={{
                        borderRadius: "20px 20px 0 0",
                        backgroundColor: "#000",
                        color: "white",
                        padding: "20px 60px",
                        textAlign: "center",
                        fontSize: "24px",
                        margin: 0,
                      }}
                    >
                      {selectedHotel}
                      {selectedHotel && getHotelStarRating(selectedHotel) && (
                        <div
                          style={{
                            fontSize: "18px",
                            marginTop: "10px",
                            color: "#FFD700",
                            fontWeight: "600",
                          }}
                        >
                          {getStarRatingDisplay(getHotelStarRating(selectedHotel))}
                        </div>
                      )}
                    </h4>
                  </div>

                  <div
                    style={{
                      width: "100%",
                      height: "12px",
                      backgroundColor: "black",
                      borderRadius: "0 0 50px 50px",
                    }}
                  />
                </div>
              </div>

              <div className="pricing-block-two col-md-4 col-sm-12">
                <div className="inner-box">
                  <div className="price-box">
                    <h4 className="price1" style={{ fontSize: "28px", color: "#9B1321" }}>
                      {loadingHotels || !hotelPrices.single ? "Loading..." : hotelPrices.single}
                    </h4>
                  </div>
                  <div className="title1" style={{ fontSize: "18px", fontWeight: "700", color: "#333" }}>
                    Single Room
                  </div>

                  {!localStorage.getItem("token") && (
                    <RegisterButton
                      onClick={() => openModalForRoom("Single Room")}
                      disabled={
                        isRegistrationClosed ||
                        !selectedHotel ||
                        !hotelPrices.single ||
                        loadingHotels
                      }
                    />
                  )}
                </div>
              </div>

              <div className="pricing-block-two col-md-4 col-sm-12">
                <div className="inner-box">
                  <div className="price-box">
                    <h4 className="price1" style={{ fontSize: "28px", color: "#9B1321" }}>
                      {loadingHotels || !hotelPrices.double ? "Loading..." : hotelPrices.double}
                    </h4>
                  </div>
                  <div className="title1" style={{ fontSize: "18px", fontWeight: "700", color: "#333" }}>
                    Double Room
                  </div>

                  {!localStorage.getItem("token") && (
                    <RegisterButton
                      onClick={() => openModalForRoom("Double Room")}
                      disabled={
                        isRegistrationClosed ||
                        !selectedHotel ||
                        !hotelPrices.double ||
                        loadingHotels
                      }
                    />
                  )}
                </div>
              </div>

              <div className="pricing-block-two col-md-4 col-sm-12">
                <div className="inner-box">
                  <div className="price-box">
                    <h4 className="price1" style={{ fontSize: "28px", color: "#9B1321" }}>
                      {loadingHotels || !hotelPrices.triple ? "Loading..." : hotelPrices.triple}
                    </h4>
                  </div>
                  <div className="title1" style={{ fontSize: "18px", fontWeight: "700", color: "#333" }}>
                    Triple Room
                  </div>

                  {!localStorage.getItem("token") && (
                    <RegisterButton
                      onClick={() => openModalForRoom("Triple Room")}
                      disabled={
                        isRegistrationClosed ||
                        !selectedHotel ||
                        !hotelPrices.triple ||
                        loadingHotels
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {isModalOpen && conferenceId && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "16px",
              maxWidth: "900px",
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 25px 50px rgba(0,0,0,0.25)",
            }}
          >
            {showForm ? (
              <>
                <div style={{ padding: "30px 40px 20px", borderBottom: "2px solid #eee" }}>
                  <h3 style={{ margin: 0, color: "#9B1321", fontSize: "28px" }}>
                    Accommodation Booking
                  </h3>

                  <div style={{ marginTop: "15px", display: "flex", gap: "30px", flexWrap: "wrap" }}>
                    <div>
                      <strong>Room:</strong> {selectedRoomType}
                    </div>

                    <div>
                      <strong>Hotel:</strong> {selectedHotel}
                      {getHotelStarRating(selectedHotel) && (
                        <div style={{ fontSize: "14px", color: "#FFD700", marginTop: "5px" }}>
                          {getStarRatingDisplay(getHotelStarRating(selectedHotel))}
                        </div>
                      )}
                    </div>

                    <div>
                      <strong>Nights: </strong>
                      <span style={{ fontWeight: "600", color: "#9B1321" }}>{nights || 0}</span>
                    </div>

                    <div style={{ color: "#9B1321", fontSize: "20px", fontWeight: "700" }}>
                      ${Number(totalAmount || 0).toFixed(2)}
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginTop: 4 }}>
                        (${Number(perNightAmount || 0).toFixed(2)} / night)
                      </div>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleFormSubmit} style={{ padding: "40px" }}>
                  <div className="row">
                    <div className="col-md-6" style={{ marginBottom: "25px" }}>
                      <label style={{ fontWeight: 700, marginBottom: "10px", display: "block" }}>
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="full_name"
                        className="form-control"
                        value={formData.full_name}
                        onChange={handleInputChange}
                        required
                        style={{ padding: "15px", borderRadius: "10px", border: "2px solid #ddd" }}
                      />
                    </div>

                    <div className="col-md-6" style={{ marginBottom: "25px" }}>
                      <label style={{ fontWeight: 700, marginBottom: "10px", display: "block" }}>
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        className="form-control"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        style={{ padding: "15px", borderRadius: "10px", border: "2px solid #ddd" }}
                      />
                    </div>

                    <div className="col-md-6" style={{ marginBottom: "25px" }}>
                      <label style={{ fontWeight: 700, marginBottom: "10px", display: "block" }}>
                        Phone *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        className="form-control"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+1234567890"
                        required
                        style={{ padding: "15px", borderRadius: "10px", border: "2px solid #ddd" }}
                      />
                    </div>

                    <div className="col-md-6" style={{ marginBottom: "25px" }}>
                      <label style={{ fontWeight: 700, marginBottom: "10px", display: "block" }}>
                        Country *
                      </label>

                      <div ref={countryDropdownRef} style={{ position: "relative" }}>
                        <button
                          type="button"
                          className="form-control"
                          onClick={() => setIsCountryOpen((p) => !p)}
                          disabled={loadingCountries}
                          style={{
                            padding: "15px",
                            borderRadius: "10px",
                            border: "2px solid #ddd",
                            background: "#fff",
                            width: "100%",
                            textAlign: "left",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            {selectedCountryObj?.flag_url ? (
                              <img
                                src={selectedCountryObj.flag_url}
                                alt="flag"
                                width={24}
                                height={18}
                                style={{
                                  borderRadius: "3px",
                                  objectFit: "cover",
                                  border: "1px solid #eee",
                                }}
                              />
                            ) : (
                              <span style={{ width: 24, height: 18 }} />
                            )}
                            <span>{formData.country || "Select country"}</span>
                          </span>
                          <span style={{ opacity: 0.7 }}>▾</span>
                        </button>

                        {isCountryOpen && (
                          <div
                            style={{
                              position: "absolute",
                              top: "calc(100% + 8px)",
                              left: 0,
                              right: 0,
                              background: "#fff",
                              border: "1px solid #ddd",
                              borderRadius: "8px",
                              maxHeight: 280,
                              overflowY: "auto",
                              zIndex: 10000,
                              boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                            }}
                          >
                            {countries.map((c) => (
                              <button
                                key={c.id}
                                type="button"
                                onClick={() => selectCountry(c.name)}
                                style={{
                                  width: "100%",
                                  padding: "14px 16px",
                                  border: "none",
                                  background: c.name === formData.country ? "#f8f9fa" : "#fff",
                                  textAlign: "left",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "12px",
                                  cursor: "pointer",
                                  transition: "background 0.2s",
                                }}
                              >
                                <img
                                  src={c.flag_url}
                                  alt="flag"
                                  width={24}
                                  height={18}
                                  style={{
                                    borderRadius: "3px",
                                    objectFit: "cover",
                                    border: "1px solid #eee",
                                  }}
                                />
                                <span>{c.name}</span>
                              </button>
                            ))}
                          </div>
                        )}

                        <input type="hidden" name="country" value={formData.country} required />
                      </div>
                    </div>

                    <div className="col-md-6" style={{ marginBottom: "25px" }}>
                      <label style={{ fontWeight: 700, marginBottom: "10px", display: "block" }}>
                        Check-in Date *
                      </label>
                      <input
                        type="date"
                        name="check_in"
                        className="form-control"
                        value={formData.check_in}
                        onChange={handleInputChange}
                        required
                        style={{ padding: "15px", borderRadius: "10px", border: "2px solid #ddd" }}
                      />
                    </div>

                    <div className="col-md-6" style={{ marginBottom: "25px" }}>
                      <label style={{ fontWeight: 700, marginBottom: "10px", display: "block" }}>
                        Check-out Date *
                      </label>
                      <input
                        type="date"
                        name="check_out"
                        className="form-control"
                        value={formData.check_out}
                        onChange={handleInputChange}
                        required
                        style={{ padding: "15px", borderRadius: "10px", border: "2px solid #ddd" }}
                      />
                    </div>

                    <div className="col-md-6" style={{ marginBottom: "25px" }}>
                      <label style={{ fontWeight: 700, marginBottom: "10px", display: "block" }}>
                        Number of Guests *
                      </label>
                      <input
                        type="number"
                        name="guests"
                        className="form-control"
                        value={formData.guests}
                        onChange={handleInputChange}
                        min="1"
                        max="4"
                        required
                        style={{ padding: "15px", borderRadius: "10px", border: "2px solid #ddd" }}
                      />
                    </div>

                    <div className="col-md-6" style={{ marginBottom: "25px" }}>
                      <label style={{ fontWeight: 700, marginBottom: "10px", display: "block" }}>
                        Room Type *
                      </label>
                      <select
                        name="room_type"
                        className="form-control"
                        value={formData.room_type}
                        onChange={handleInputChange}
                        required
                        style={{ padding: "15px", borderRadius: "10px", border: "2px solid #ddd" }}
                      >
                        <option value="">Select Room Type</option>
                        <option value="Single Room">Single Room</option>
                        <option value="Double Room">Double Room</option>
                        <option value="Triple Room">Triple Room</option>
                      </select>
                    </div>

                    <div className="col-md-6" style={{ marginBottom: "25px" }}>
                      <label style={{ fontWeight: 700, marginBottom: "10px", display: "block" }}>
                        Hotel
                      </label>
                      <input
                        type="text"
                        name="hotel_choice"
                        className="form-control"
                        value={formData.hotel_choice}
                        readOnly
                        style={{
                          padding: "15px",
                          borderRadius: "10px",
                          border: "2px solid #9B1321",
                          background: "#f8f9fa",
                        }}
                      />
                    </div>

                    <div className="col-md-6" style={{ marginBottom: "25px" }}>
                      <label style={{ fontWeight: 700, marginBottom: "10px", display: "block" }}>
                        Bed Preference
                      </label>
                      <select
                        name="bed_type"
                        className="form-control"
                        value={formData.bed_type}
                        onChange={handleInputChange}
                        style={{ padding: "15px", borderRadius: "10px", border: "2px solid #ddd" }}
                      >
                        <option value="">No Preference</option>
                        {BED_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-12" style={{ marginBottom: "25px" }}>
                      <label style={{ fontWeight: 700, marginBottom: "10px", display: "block" }}>
                        Special Requirements
                      </label>
                      <select
                        name="special_requirements"
                        className="form-control"
                        value={formData.special_requirements}
                        onChange={handleInputChange}
                        style={{ padding: "15px", borderRadius: "10px", border: "2px solid #ddd" }}
                      >
                        <option value="">None</option>
                        {SPECIAL_REQ_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>

                      {formData.special_requirements === "Other" && (
                        <input
                          type="text"
                          name="other_text"
                          className="form-control"
                          placeholder="Please specify your requirements..."
                          value={formData.other_text}
                          onChange={handleInputChange}
                          style={{
                            marginTop: "12px",
                            padding: "15px",
                            borderRadius: "10px",
                            border: "2px solid #ddd",
                          }}
                        />
                      )}
                    </div>

                    <div className="col-md-12" style={{ marginBottom: "30px" }}>
                      <label style={{ display: "flex", alignItems: "flex-start", gap: "12px", fontWeight: 700 }}>
                        <input
                          type="checkbox"
                          name="terms_accepted"
                          checked={formData.terms_accepted}
                          onChange={handleInputChange}
                          required
                          style={{ marginTop: "4px", width: "20px", height: "20px" }}
                        />
                        <span style={{ lineHeight: "1.4", fontWeight: "500" }}>
                          I confirm that all information provided is accurate. I understand that hotel
                          reservation and invoice follow the selected hotel's{" "}
                          <a href="/terms-and-conditions" style={{ textDecoration: "underline" }}>
                            terms and policies.
                          </a>
                        </span>
                      </label>
                    </div>
                  </div>

                  <div style={{ display: "flex", justifyContent: "flex-end", gap: "20px", marginTop: "30px" }}>
                    <button
                      type="button"
                      className="theme-btn btn-style-two"
                      onClick={closeModal}
                      style={{ padding: "15px 40px" }}
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      className="theme-btn btn-style-one"
                      disabled={submitting}
                      style={{ padding: "15px 40px", backgroundColor: "#9B1321" }}
                    >
                      {submitting ? "Processing..." : "Confirm Booking"}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              showInvoice &&
              bookingData && (
                <>
                  <div
                    style={{
                      padding: "40px",
                      textAlign: "center",
                      background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                    }}
                  >
                    <h2 style={{ color: "#9B1321", marginBottom: "20px" }}>
                      Booking Confirmed!
                    </h2>

                    <div
                      style={{
                        background: "white",
                        padding: "30px",
                        borderRadius: "16px",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
                        maxWidth: "500px",
                        margin: "0 auto",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "24px",
                          fontWeight: "700",
                          color: "#10b981",
                          marginBottom: "20px",
                        }}
                      >
                        Booking ID: #{bookingData.booking_id}
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "20px",
                          marginBottom: "30px",
                        }}
                      >
                        <div>
                          <h4 style={{ color: "#9B1321", marginBottom: "15px" }}>Customer</h4>
                          <p><strong>Name:</strong> {bookingData.full_name}</p>
                          <p><strong>Email:</strong> {bookingData.email}</p>
                          <p><strong>Phone:</strong> {bookingData.phone}</p>
                        </div>

                        <div>
                          <h4 style={{ color: "#9B1321", marginBottom: "15px" }}>Booking</h4>
                          <p><strong>Room:</strong> {bookingData.room_type}</p>
                          <p><strong>Hotel:</strong> {bookingData.hotel_choice}</p>
                          <p><strong>Check-in:</strong> {bookingData.check_in}</p>
                          <p><strong>Check-out:</strong> {bookingData.check_out}</p>
                          <p><strong>Nights:</strong> {bookingData.nights}</p>
                        </div>
                      </div>

                      <div
                        style={{
                          background: "linear-gradient(135deg, #9B1321 0%, #a81c22 100%)",
                          color: "white",
                          padding: "30px",
                          borderRadius: "16px",
                          marginBottom: "30px",
                        }}
                      >
                        <h3 style={{ margin: "0 0 10px 0", fontSize: "36px" }}>
                          ${Number(bookingData.amount || 0).toFixed(2)}
                        </h3>
                        <p style={{ margin: 0, fontSize: "16px" }}>
                          Total Amount ({bookingData.nights} nights)
                        </p>

                        {Number.isFinite(bookingData.per_night) && (
                          <p style={{ margin: "10px 0 0 0", fontSize: "14px", opacity: 0.9 }}>
                            (${Number(bookingData.per_night).toFixed(2)} / night)
                          </p>
                        )}
                      </div>

                      <div style={{ display: "flex", gap: "15px", justifyContent: "center" }}>
                        <button
                          type="button"
                          className="theme-btn btn-style-two"
                          onClick={closeModal}
                          style={{ padding: "15px 30px" }}
                        >
                          Close
                        </button>

                        <button
                          type="button"
                          className="theme-btn btn-style-one"
                          onClick={handlePayNow}
                          disabled={loading}
                          style={{ padding: "15px 30px", backgroundColor: "#10b981" }}
                        >
                          {loading
                            ? "Processing..."
                            : `Pay Now - $${Number(bookingData.amount || 0).toFixed(2)}`}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AccommodationPrices;